import api from './api';
import type {
  DiskussionChecklist,
  DiskussionChecklistItem,
  DiskussionKategorie,
  DiskussionLatestPost,
  DiskussionLinkSuggestion,
  DiskussionReactionSummary,
  DiskussionRefTypeInfo,
  DiskussionThreadDetail,
  DiskussionThreadList,
  DiskussionPost,
} from '../types';

export interface DiskussionAnkerIn {
  ref_type: string;
  ref_id?: number | null;
  hof_jahr?: number | null;
  hof_monat?: number | null;
  hof_mitarbeiter_id?: number | null;
}

export const diskussionService = {
  listRefTypes: async (): Promise<DiskussionRefTypeInfo[]> => {
    const r = await api.get('/diskussion/ref-types');
    return r.data;
  },

  linkSuggestions: async (q: string, limit = 15): Promise<DiskussionLinkSuggestion[]> => {
    const r = await api.get('/diskussion/link-suggestions', { params: { q, limit } });
    return r.data;
  },

  listKategorien: async (): Promise<DiskussionKategorie[]> => {
    const r = await api.get('/diskussion/kategorien');
    return r.data;
  },

  listLatestPosts: async (limit = 6): Promise<DiskussionLatestPost[]> => {
    const r = await api.get('/diskussion/latest-posts', { params: { limit } });
    return r.data;
  },

  listThreads: async (
    kategorieId: number,
    params?: {
      ref_type?: string;
      ref_id?: number;
      hof_jahr?: number;
      hof_monat?: number;
      hof_mitarbeiter_id?: number;
      new_since_hours?: number;
    },
  ): Promise<DiskussionThreadList[]> => {
    const r = await api.get(`/diskussion/kategorien/${kategorieId}/threads`, { params });
    return r.data;
  },

  getThread: async (threadId: number): Promise<DiskussionThreadDetail> => {
    const r = await api.get(`/diskussion/threads/${threadId}`);
    return r.data;
  },

  createThread: async (
    kategorieId: number,
    data: { titel: string; first_post_body: string; anker: DiskussionAnkerIn[] },
  ): Promise<DiskussionThreadDetail> => {
    const r = await api.post(`/diskussion/kategorien/${kategorieId}/threads`, data);
    return r.data;
  },

  createPost: async (threadId: number, body: string): Promise<DiskussionPost> => {
    const r = await api.post(`/diskussion/threads/${threadId}/posts`, { body });
    return r.data;
  },

  updatePost: async (threadId: number, postId: number, body: string): Promise<DiskussionPost> => {
    const r = await api.patch(`/diskussion/threads/${threadId}/posts/${postId}`, { body });
    return r.data;
  },

  deletePost: async (threadId: number, postId: number): Promise<void> => {
    await api.delete(`/diskussion/threads/${threadId}/posts/${postId}`);
  },

  uploadBild: async (file: File): Promise<{ file_id: number; insert_marker: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const r = await api.post('/diskussion/upload-bild', formData);
    return r.data;
  },

  getChecklist: async (postId: number): Promise<DiskussionChecklist> => {
    const r = await api.get(`/diskussion/posts/${postId}/checklist`);
    return r.data;
  },

  addChecklistItem: async (postId: number, text: string): Promise<DiskussionChecklistItem> => {
    const r = await api.post(`/diskussion/posts/${postId}/checklist/items`, { text });
    return r.data;
  },

  updateChecklistItem: async (
    postId: number,
    itemId: number,
    data: { text?: string; position?: number },
  ): Promise<DiskussionChecklistItem> => {
    const r = await api.patch(`/diskussion/posts/${postId}/checklist/items/${itemId}`, data);
    return r.data;
  },

  deleteChecklistItem: async (postId: number, itemId: number): Promise<void> => {
    await api.delete(`/diskussion/posts/${postId}/checklist/items/${itemId}`);
  },

  markChecklistItemDone: async (
    postId: number,
    itemId: number,
  ): Promise<DiskussionChecklistItem> => {
    const r = await api.put(`/diskussion/posts/${postId}/checklist/items/${itemId}/done`);
    return r.data;
  },

  unmarkChecklistItemDone: async (
    postId: number,
    itemId: number,
  ): Promise<DiskussionChecklistItem> => {
    const r = await api.delete(`/diskussion/posts/${postId}/checklist/items/${itemId}/done`);
    return r.data;
  },

  setReaction: async (postId: number, emoji: string): Promise<DiskussionReactionSummary[]> => {
    const r = await api.put(`/diskussion/posts/${postId}/reactions`, { emoji });
    return r.data;
  },

  removeReaction: async (postId: number): Promise<DiskussionReactionSummary[]> => {
    const r = await api.delete(`/diskussion/posts/${postId}/reactions`);
    return r.data;
  },

  watchThread: async (threadId: number): Promise<void> => {
    await api.put(`/diskussion/threads/${threadId}/watch`);
  },

  unwatchThread: async (threadId: number): Promise<void> => {
    await api.delete(`/diskussion/threads/${threadId}/watch`);
  },

  patchThread: async (
    threadId: number,
    data: { locked?: boolean; pinned?: boolean },
  ): Promise<DiskussionThreadDetail> => {
    const r = await api.patch(`/diskussion/threads/${threadId}`, data);
    return r.data;
  },
};
